import { useEffect, useRef, useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { useTheme } from '@hivebrite/design-system/Theme';
import { toast } from '@hivebrite/design-system/Toast';

import { AISummaryButton } from 'hb-react/frontoffice/apps/Forum/components/AISummaryButton';
import { useGenerateSummary } from 'hb-react/frontoffice/apps/Forum/hooks/queries/useGenerateSummary';
import { AIWrapper } from 'hb-react/shared/components/AIWrapper';
import { HtmlContent } from 'hb-react/shared/components/HtmlContent';
import { captureException } from 'hb-react/shared/utils/errorReporting';

const MINIMAL_DELAY = 1500;

interface DiscussionSummaryProps {
  discussionId: number;
}

export const DiscussionSummary = ({ discussionId }: DiscussionSummaryProps) => {
  const theme = useTheme();
  const {
    data: summary,
    isFetching,
    refetch,
  } = useGenerateSummary(discussionId);
  const [shouldShowSummaryWithDelay, setShouldShowSummaryWithDelay] =
    useState(false);
  const userStartsFetchingAt = useRef<number>();

  // Reset state when discussionId changes
  useEffect(() => {
    setShouldShowSummaryWithDelay(false);
    userStartsFetchingAt.current = undefined;
  }, [discussionId]);

  // We want the user to feel that AI is "cooking" the summary. We do that by
  // ensuring that the delay between triggering the query and showing the summary
  // is at least MINIMAL_DELAY.
  useEffect(() => {
    if (summary && !isFetching && userStartsFetchingAt.current) {
      const elapsedTime = Date.now() - userStartsFetchingAt.current;
      const remainingDelay = Math.max(0, MINIMAL_DELAY - elapsedTime);

      const timer = setTimeout(() => {
        setShouldShowSummaryWithDelay(true);
        userStartsFetchingAt.current = undefined;
      }, remainingDelay);

      return () => clearTimeout(timer);
    } else if (summary && !isFetching) {
      // Cached summary - show immediately
      setShouldShowSummaryWithDelay(true);
    } else {
      setShouldShowSummaryWithDelay(false);
    }
  }, [summary, isFetching]);

  const handleGenerateSummary = async () => {
    if (isFetching || summary) {
      return;
    }

    userStartsFetchingAt.current = Date.now();

    try {
      await refetch();
    } catch (error) {
      toast.error(__('Sorry, an error occurred'));
      captureException(error);
      userStartsFetchingAt.current = undefined;
    }
  };

  const showSummary = summary && shouldShowSummaryWithDelay;
  const isButtonLoading =
    isFetching || Boolean(summary && !shouldShowSummaryWithDelay);

  if (showSummary) {
    return (
      <Box mb={4}>
        <AIWrapper id="discussion-summary" trigger={shouldShowSummaryWithDelay}>
          <Box flexDirection="column">
            <Box mb={2} alignItems="center">
              <FontAwesomeIcon
                height="16px"
                iconName="sparkles"
                iconType={CATEGORIES.SOLID}
                color={theme.colors.primary}
              />
              <Box ml={2} font="body-semibold-asphalt" color="asphalt">
                {s__('Forum|AI Summary')}
              </Box>
            </Box>
            <Box font="body-regular-asphalt">
              <HtmlContent content={summary} stylesConfig="web" />
            </Box>
          </Box>
        </AIWrapper>
      </Box>
    );
  }

  return (
    <Box mb={4}>
      <AISummaryButton
        isLoading={isButtonLoading}
        label={s__('Forum|Summarize with AI')}
        onClick={handleGenerateSummary}
        loadingText={s__('Forum|Summarizing with AI...')}
      />
    </Box>
  );
};
